import { getDownloadHeadersWithCurrentAccountConsumerId } from 'lib/downloads/utils';
import { Dispatch } from 'redux';
import FileServices from 'services/FileServices';
import { CacheTypes } from 'modules/types/mobileCaching';
import {
  allergiesDownloadingSelector,
  allergiesRequestStateSelector
} from 'store/medicalHistory/allergies/selectors';
import { messageReply, messageShare } from 'store/messaging/actions';
import { currentConsumerPcpDoctorsSelector } from 'store/myDoctors/selectors';
import { RootState, Thunk } from 'store/types';
import { oc } from 'ts-optchain';
import { DFDHeadersObject, DispatcherResource, DispatcherEndpoint } from 'modules/types/common';
import { DFDHeaders } from 'serverTypes/authentication';
import { getMedicalHistoryEndpointGenerator } from '../common';
import { TransmitSources } from '../../transmit';
import { Allergy } from '../types';

export const GET_ALLERGIES_CACHE_SUCCESS = 'ALLERGIES.GET_ALLERGIES_CACHE_SUCCESS';

export const GET_ALLERGIES = 'ALLERGIES.GET_ALLERGIES';
export const GET_ALLERGIES_SUCCESS = 'ALLERGIES.GET_ALLERGIES_SUCCESS';
export const GET_ALLERGIES_FAIL = 'ALLERGIES.GET_ALLERGIES_FAIL';

const getEndpoint = getMedicalHistoryEndpointGenerator(DispatcherResource.ALLERGIES);

export const getAllergies = (useRequestState = true) => ({
  type: GET_ALLERGIES,
  payload: {
    useRequestState,
    requestStateSelector: allergiesRequestStateSelector,
    cacheType: CacheTypes.ALLERGIES,
    cacheRetrievalSuccessAction: GET_ALLERGIES_CACHE_SUCCESS,
    client: 'dispatcher',
    request: {
      method: 'get',
      url: getEndpoint()
    }
  }
});

export const replyToAllergies = (
  allergies: Allergy[],
  isShare = false,
  returnBackTo?: string
): Thunk => (dispatch, getState) => {
  const consumerPcpList = currentConsumerPcpDoctorsSelector(getState());

  if (!allergies) {
    return;
  }

  const params = {
    subject: 'Medical History',
    readonly: allergies
      .map(a => `\nid: ${a.id}\nAllergic to: ${a.name}\n(last updated on: ${a.lastUpdated})`)
      .join(`,\n`),
    transmitParams: {
      source: TransmitSources.Allergies,
      payload: allergies,
      returnBackTo
    }
  };

  if (isShare) {
    dispatch(messageShare(params));
  } else {
    dispatch(messageReply(params, oc(consumerPcpList)[0].cernerPersonnelId('')));
  }
};

/**
 * Share Allergies
 */
export const SHARE_ALLERGIES = 'ALLERGIES.SHARE_ALLERGIES';
export const SHARE_ALLERGIES_SUCCESS = 'ALLERGIES.SHARE_ALLERGIES_SUCCESS';
export const SHARE_ALLERGIES_FAIL = 'ALLERGIES.SHARE_ALLERGIES_FAIL';

export const shareAllergies = (
  allergies: any,
  toAddress: string,
  message: string,
  useSecureEmail: boolean,
  contactPhoneNumber: string,
  subject: string
) => ({
  type: SHARE_ALLERGIES,
  payload: {
    client: 'dispatcher',
    request: {
      method: 'post',
      url: getEndpoint(DispatcherEndpoint.TRANSMIT),
      data: {
        toAddress,
        message,
        useSecureEmail,
        contactPhoneNumber,
        subject
      }
    }
  }
});

/**
 * Download Allergies
 */
export const DOWNLOAD_ALLERGIES = 'ALLERGIES.DOWNLOAD_ALLERGIES';
export const DOWNLOAD_ALLERGIES_SUCCESS = 'ALLERGIES.DOWNLOAD_ALLERGIES_SUCCESS';
export const DOWNLOAD_ALLERGIES_FAIL = 'ALLERGIES.DOWNLOAD_ALLERGIES_FAIL';

export const downloadAllergies = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();

  // Check for in flight downloads if there are any then return
  if (allergiesDownloadingSelector(state) === true) {
    return;
  }

  dispatch({ type: DOWNLOAD_ALLERGIES, payload: {} });

  const timestamp = Date.now();

  try {
    const uri = `${getEndpoint(DispatcherEndpoint.DOWNLOAD, true)}`;

    const headers: DFDHeadersObject = {
      [DFDHeaders.mhpAccept]: 'application/pdf',
      ...getDownloadHeadersWithCurrentAccountConsumerId(state, uri)
    };
    const response = await FileServices.download(`allergies-${timestamp}.pdf`, uri, headers);

    dispatch({ type: DOWNLOAD_ALLERGIES_SUCCESS, payload: { response } });
  } catch (err) {
    dispatch({ type: DOWNLOAD_ALLERGIES_FAIL, error: err });
  }
};
